// src/components/Login.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Login.css';
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from 'react-i18next';
import {BASE_URL} from "../config";

const Login = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [verifyCode, setVerifyCode] = useState(['', '', '', '', '', '']);
    const [forgotPasswordStep, setForgotPasswordStep] = useState(0); // 0: email, 1: code, 2: new password
    const [forgotPasswordData, setForgotPasswordData] = useState({
        email: '',
        code: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [forgotPasswordCode, setForgotPasswordCode] = useState(['', '', '', '', '', '']);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false); // New state for forgot password modal
    const { login } = useAuth();
    const navigate = useNavigate();

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Validate email format
    const validateEmail = (email) => {
        const regex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
        return regex.test(email);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle login submission
    const handleLogin = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);

        try {
            const response = await fetch(`${BASE_URL}api/v1/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            // Get the response text first to see what's coming back
            const responseText = await response.text();
            console.log('Response text:', responseText);

            let responseData;
            try {
                responseData = JSON.parse(responseText);
            } catch (parseError) {
                console.error('Failed to parse JSON:', parseError);
                throw new Error(`Server returned invalid JSON: ${responseText}`);
            }

            if (response.ok) {
                console.log('Login successful:', responseData);

                // Use the login function from AuthContext instead of manually setting localStorage
                login(responseData);

                // Redirect based on whether user was trying to checkout
                const redirectToCheckout = localStorage.getItem('placeOrder') === 'true';
                navigate(redirectToCheckout ? '/' : '/');

            } else {
                console.log('Login failed:', responseData);

                if (response.status === 409) {
                    // Email not verified - show verification modal
                    setShowVerifyModal(true);
                } else {
                    setErrors({
                        general: responseData.message || responseData.error || `Login failed (${response.status})`
                    });
                }
            }
        } catch (error) {
            console.error('Login error:', error);
            setErrors({ general: error.message || 'Network error. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle verify code input
    const handleVerifyCodeChange = (index, value) => {
        if (value.length > 1) return;

        const newCode = [...verifyCode];
        newCode[index] = value;
        setVerifyCode(newCode);

        // Auto-focus next input
        if (value && index < 5) {
            document.getElementById(`verify-code-${index + 1}`).focus();
        }
    };

    // Handle forgot password code input
    const handleForgotPasswordCodeChange = (index, value) => {
        if (value.length > 1) return;

        const newCode = [...forgotPasswordCode];
        newCode[index] = value;
        setForgotPasswordCode(newCode);

        // Auto-focus next input
        if (value && index < 5) {
            document.getElementById(`forgot-code-${index + 1}`).focus();
        }
    };

    // Handle verification code submission
    const handleVerifyEmail = async () => {
        const code = verifyCode.join('');

        if (code.length !== 6) {
            setErrors({ verify: 'Please enter the complete verification code' });
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}api/v1/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    code: code
                }),
            });

            if (response.ok) {
                setShowVerifyModal(false);
                setVerifyCode(['', '', '', '', '', '']);
                setErrors({ general: 'Email verified successfully. You can now login.' });
            } else {
                setErrors({ verify: 'Invalid verification code. Please try again.' });
            }
        } catch (error) {
            setErrors({ verify: 'Verification failed. Please try again.' });
        }
    };

    // Resend verification code
    const handleResendCode = async () => {
        try {
            await fetch(`${BASE_URL}api/v1/send-code-again`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: formData.email }),
            });

            setErrors({ verify: 'Verification code sent successfully.' });
        } catch (error) {
            setErrors({ verify: 'Failed to send code. Please try again.' });
        }
    };

    // Forgot password handlers
    const handleForgotPassword = async () => {
        if (!validateEmail(forgotPasswordData.email)) {
            setErrors({ forgot: 'Please enter a valid email address' });
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}api/v1/forget-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: forgotPasswordData.email }),
            });

            if (response.ok) {
                setForgotPasswordStep(1);
                setErrors({});
            } else {
                setErrors({ forgot: 'Email not associated with any account.' });
            }
        } catch (error) {
            setErrors({ forgot: 'Failed to process request. Please try again.' });
        }
    };

    // Verify forgot password code
    const handleVerifyForgotPasswordCode = async () => {
        const code = forgotPasswordCode.join('');

        if (code.length !== 6) {
            setErrors({ forgot: 'Please enter the complete verification code' });
            return;
        }

        try {
            // In a real implementation, you would verify the code with your backend
            // For now, we'll just move to the next step
            setForgotPasswordStep(2);
            setForgotPasswordData(prev => ({ ...prev, code }));
            setErrors({});
        } catch (error) {
            setErrors({ forgot: 'Invalid verification code. Please try again.' });
        }
    };

    // Reset password
    const handleResetPassword = async () => {
        if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
            setErrors({ forgot: 'Passwords do not match' });
            return;
        }

        if (forgotPasswordData.newPassword.length < 8) {
            setErrors({ forgot: 'Password must be at least 8 characters' });
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}api/v1/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: forgotPasswordData.email,
                    code: forgotPasswordCode.join(''), // Use the actual code from input
                    password: forgotPasswordData.newPassword,
                    password_confirmation: forgotPasswordData.confirmPassword // Laravel expects this naming
                }),
            });

            const responseData = await response.json();

            if (response.ok) {
                // Close modal and reset state
                setShowForgotPasswordModal(false);
                setForgotPasswordStep(0);
                setForgotPasswordData({
                    email: '',
                    code: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setForgotPasswordCode(['', '', '', '', '', '']);
                setErrors({ general: 'Password reset successfully. You can now login.' });
            } else {
                // Handle validation errors
                if (responseData.errors) {
                    setErrors({ forgot: Object.values(responseData.errors).join(' ') });
                } else {
                    setErrors({ forgot: responseData.message || 'Failed to reset password.' });
                }
            }
        } catch (error) {
            setErrors({ forgot: 'Network error. Please try again.' });
        }
    };

    // Close forgot password modal and reset state
    const handleCloseForgotPasswordModal = () => {
        setShowForgotPasswordModal(false);
        setForgotPasswordStep(0);
        setForgotPasswordData({
            email: '',
            code: '',
            newPassword: '',
            confirmPassword: ''
        });
        setForgotPasswordCode(['', '', '', '', '', '']);
        setErrors({});
    };

    // Open forgot password modal
    const handleOpenForgotPasswordModal = () => {
        setShowForgotPasswordModal(true);
    };

    return (
        <div className="login-page">
            {/* Login Form */}
            <div className="login-form container section-padding">
                <div className="row">
                    <div className="col-md-8 col-lg-6 mx-auto text-center">
                        <h2 className="text-color-bright mb-5">{t('Login to your account')}</h2>

                        {errors.general && (
                            <div className="alert alert-danger" role="alert">
                                {errors.general}
                            </div>
                        )}

                        <form onSubmit={handleLogin}>
                            <div className="form-floating mb-3">
                                <input
                                    type="email"
                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                    id="emailInput"
                                    placeholder="name@example.com"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="emailInput">{t('Email Address')}</label>
                                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                            </div>

                            <div className="form-floating position-relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                    id="passwordInput"
                                    placeholder="Password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="passwordInput">{t('Password')}</label>
                                <button
                                    type="button"
                                    className="show-hide-pass-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye-off" width="24" height="24" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6f32be" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                            <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
                                            <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" />
                                            <path d="M3 3l18 18" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye" width="24" height="24" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6f32be" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                            <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                                        </svg>
                                    )}
                                </button>
                                {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                            </div>

                            <div className="forgot-password mt-1 text-end">
                                <button
                                    type="button"
                                    className="bg-transparent p-0 text-primary border-0 text-decoration-underline"
                                    onClick={handleOpenForgotPasswordModal}
                                >
                                    {t('Forgot Password?')}
                                </button>
                            </div>

                            <button
                                type="submit"
                                className="btn btn-custom mt-3 py-2 px-4"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Logging in...' : `${t('Login')}`}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            <div className="new-account w-100 my-4 text-center">
                {t('Do not Have an Account? Create')} <Link to="/register">{t('New Account Here')}</Link>
            </div>

            {/* Verify Email Modal */}
            {showVerifyModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content bg-light-color border-0">
                            <div className="modal-header">
                                <h5 className="modal-title text-color-bright">Verify Email Now</h5>
                                <button
                                    type="button"
                                    className="bg-transparent p-0 border-0"
                                    onClick={() => setShowVerifyModal(false)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-x" width="24" height="24" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6f32be" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M18 6l-12 12" />
                                        <path d="M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="modal-body verify-email-modal-body">
                <span className="text-main-muted">
                  Please enter the code that we sent to your email address.
                </span>

                                {errors.verify && (
                                    <div className="alert alert-danger mt-3" role="alert">
                                        {errors.verify}
                                    </div>
                                )}

                                <div className="verify-otp-field mt-3">
                                    {verifyCode.map((digit, index) => (
                                        <input
                                        key={index}
                                        type="text"
                                        maxLength="1"
                                        value={digit}
                                        onChange={(e) => handleVerifyCodeChange(index, e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Backspace" && !verifyCode[index] && index > 0) {
                                            document.getElementById(`verify-code-${index - 1}`).focus();
                                            }
                                        }}
                                        id={`verify-code-${index}`}
                                        className={errors.verify ? 'is-invalid' : ''}
                                        />
                                    ))}
                                </div>

                                <div className="text-center mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-link"
                                        onClick={handleResendCode}
                                    >
                                        Resend Code
                                    </button>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-close-modal"
                                    onClick={() => setShowVerifyModal(false)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-forgot-pass"
                                    onClick={handleVerifyEmail}
                                >
                                    Verify
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Forgot Password Modal */}
            {showForgotPasswordModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content bg-light-color border-0">
                            <div className="modal-header">
                                <h5 className="modal-title text-color-bright">Forgot Password</h5>
                                <button
                                    type="button"
                                    className="bg-transparent p-0 border-0"
                                    onClick={handleCloseForgotPasswordModal}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-x" width="24" height="24" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6f32be" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M18 6l-12 12" />
                                        <path d="M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="modal-body">
                                {/* Step 1: Email Input */}
                                {forgotPasswordStep === 0 && (
                                    <>
                                        <span className="text-main-muted">
                                            Enter the email address associated with your account.
                                        </span>

                                        {errors.forgot && (
                                            <div className="alert alert-danger mt-3" role="alert">
                                                {errors.forgot}
                                            </div>
                                        )}

                                        <div className="form-floating my-3">
                                            <input
                                                type="email"
                                                className="form-control text-color-bright"
                                                id="forgotEmailInput"
                                                placeholder="name@example.com"
                                                value={forgotPasswordData.email}
                                                onChange={(e) => setForgotPasswordData({
                                                    ...forgotPasswordData,
                                                    email: e.target.value
                                                })}
                                            />
                                            <label htmlFor="forgotEmailInput">Email address</label>
                                        </div>
                                    </>
                                )}

                                {/* Step 2: Code Verification */}
                                {forgotPasswordStep === 1 && (
                                    <>
                                        <span className="text-main-muted">
                                            Please enter the code we've sent to your e-mail address.
                                        </span>

                                        {errors.forgot && (
                                            <div className="alert alert-danger mt-3" role="alert">
                                                {errors.forgot}
                                            </div>
                                        )}

                                        <div className="otp-field mt-2">
                                            {forgotPasswordCode.map((digit, index) => (
                                                <input
                                                    key={index}
                                                    type="text"
                                                    maxLength="1"
                                                    value={digit}
                                                    onChange={(e) => handleForgotPasswordCodeChange(index, e.target.value)}
                                                    id={`forgot-code-${index}`}
                                                    disabled={index > 0 && forgotPasswordCode[index - 1] === ''}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}

                                {/* Step 3: New Password */}
                                {forgotPasswordStep === 2 && (
                                    <>
                                        <span className="text-main-muted">
                                            Please enter your new password.
                                        </span>

                                        {errors.forgot && (
                                            <div className="alert alert-danger mt-3" role="alert">
                                                {errors.forgot}
                                            </div>
                                        )}

                                        <div className="form-floating my-3">
                                            <input
                                                type="password"
                                                className="form-control"
                                                id="newPassInput"
                                                placeholder="Password"
                                                value={forgotPasswordData.newPassword}
                                                onChange={(e) => setForgotPasswordData({
                                                    ...forgotPasswordData,
                                                    newPassword: e.target.value
                                                })}
                                            />
                                            <label htmlFor="newPassInput">New Password</label>
                                        </div>
                                        <div className="form-floating my-3">
                                            <input
                                                type="password"
                                                className="form-control"
                                                id="conNewPassInput"
                                                placeholder="Password"
                                                value={forgotPasswordData.confirmPassword}
                                                onChange={(e) => setForgotPasswordData({
                                                    ...forgotPasswordData,
                                                    confirmPassword: e.target.value
                                                })}
                                            />
                                            <label htmlFor="conNewPassInput">Confirm Password</label>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-close-modal"
                                    onClick={handleCloseForgotPasswordModal}
                                >
                                    Close
                                </button>

                                {forgotPasswordStep === 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-forgot-pass"
                                        onClick={handleForgotPassword}
                                    >
                                        Send
                                    </button>
                                )}

                                {forgotPasswordStep === 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-forgot-pass"
                                        onClick={handleVerifyForgotPasswordCode}
                                    >
                                        Verify
                                    </button>
                                )}

                                {forgotPasswordStep === 2 && (
                                    <button
                                        type="button"
                                        className="btn btn-forgot-pass"
                                        onClick={handleResetPassword}
                                    >
                                        Save
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;